import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import MediaField from "@/components/admin/MediaField";
import SlideListField from "@/components/admin/SlideListField";
import SeoListField from "@/components/admin/SeoListField";
import AiToolbar from "@/components/admin/AiToolbar";
import LogoPicker from "@/components/admin/LogoPicker";
import SpotlightField from "@/components/admin/SpotlightField";

export default function Field({ field, value, onChange, context, record }) {
  const { type, label, key } = field;

  if (type === "spotlight") {
    return <SpotlightField value={value} onChange={onChange} />;
  }

  if (type === "logo") {
    return (
      <div className="space-y-3 md:col-span-2">
        <MediaField label={label} value={value} onChange={onChange} accept="image" testKey={key} />
        <LogoPicker brand={record?.name || ""} testKey={key} onPick={onChange} />
      </div>
    );
  }

  if (type === "image" || type === "media") {
    return (
      <MediaField
        label={label}
        value={value}
        onChange={onChange}
        accept={field.accept || "image"}
        testKey={key}
      />
    );
  }

  if (type === "slidelist") {
    return <SlideListField label={label} value={value} onChange={onChange} />;
  }

  if (type === "seolist") {
    return <SeoListField label={label} value={value} onChange={onChange} />;
  }

  if (type === "loc" || type === "locarea") {
    const v = value || { en: "", ar: "" };
    const Tag = type === "locarea" ? Textarea : Input;
    return (
      <div className={type === "locarea" ? "md:col-span-2" : ""}>
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          <Tag
            rows={type === "locarea" ? 3 : undefined}
            value={v.en}
            placeholder="English"
            data-testid={`field-${key}-en`}
            onChange={(e) => onChange({ ...v, en: e.target.value })}
          />
          <Tag
            rows={type === "locarea" ? 3 : undefined}
            dir="rtl"
            value={v.ar}
            placeholder="العربية"
            data-testid={`field-${key}-ar`}
            onChange={(e) => onChange({ ...v, ar: e.target.value })}
          />
        </div>
        {field.ai !== false && (
          <AiToolbar
            fieldLabel={label}
            value={v}
            onChange={onChange}
            context={context}
            testKey={key}
            allowExpand={type === "locarea"}
            allowSeo={key.includes("seo_description")}
          />
        )}
      </div>
    );
  }

  if (type === "loclist") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="md:col-span-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <button type="button" data-testid={`field-${key}-add`} onClick={() => onChange([...arr, { en: "", ar: "" }])} className="text-xs font-semibold text-primary hover:underline">
            + Add item
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {arr.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={item?.en || ""}
                placeholder="EN"
                data-testid={`field-${key}-en-${i}`}
                onChange={(e) => {
                  const c = [...arr];
                  c[i] = { ...(item || {}), en: e.target.value };
                  onChange(c);
                }}
              />
              <Input
                dir="rtl"
                value={item?.ar || ""}
                placeholder="AR"
                data-testid={`field-${key}-ar-${i}`}
                onChange={(e) => {
                  const c = [...arr];
                  c[i] = { ...(item || {}), ar: e.target.value };
                  onChange(c);
                }}
              />
              <button type="button" data-testid={`field-${key}-remove-${i}`} onClick={() => onChange(arr.filter((_, x) => x !== i))} className="px-2 text-xs text-destructive hover:underline">
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "statlist") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="md:col-span-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <button type="button" data-testid={`field-${key}-add`} onClick={() => onChange([...arr, { value: "", label: { en: "", ar: "" } }])} className="text-xs font-semibold text-primary hover:underline">
            + Add stat
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {arr.map((item, i) => (
            <div key={i} className="grid grid-cols-[90px_1fr_1fr_64px] items-center gap-2">
              <Input
                value={item?.value || ""}
                placeholder="700+"
                data-testid={`field-${key}-value-${i}`}
                onChange={(e) => {
                  const c = [...arr];
                  c[i] = { ...(item || {}), value: e.target.value };
                  onChange(c);
                }}
              />
              <Input
                value={item?.label?.en || ""}
                placeholder="EN label"
                data-testid={`field-${key}-en-${i}`}
                onChange={(e) => {
                  const c = [...arr];
                  c[i] = { ...(item || {}), label: { ...(item?.label || {}), en: e.target.value } };
                  onChange(c);
                }}
              />
              <Input
                dir="rtl"
                value={item?.label?.ar || ""}
                placeholder="AR label"
                data-testid={`field-${key}-ar-${i}`}
                onChange={(e) => {
                  const c = [...arr];
                  c[i] = { ...(item || {}), label: { ...(item?.label || {}), ar: e.target.value } };
                  onChange(c);
                }}
              />
              <button type="button" data-testid={`field-${key}-remove-${i}`} onClick={() => onChange(arr.filter((_, x) => x !== i))} className="text-xs text-destructive hover:underline">
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "strlist") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="md:col-span-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <button type="button" data-testid={`field-${key}-add`} onClick={() => onChange([...arr, ""])} className="text-xs font-semibold text-primary hover:underline">
            + Add
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {arr.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={item || ""}
                placeholder={field.placeholder || ""}
                data-testid={`field-${key}-${i}`}
                onChange={(e) => {
                  const c = [...arr];
                  c[i] = e.target.value;
                  onChange(c);
                }}
              />
              <button type="button" data-testid={`field-${key}-remove-${i}`} onClick={() => onChange(arr.filter((_, x) => x !== i))} className="px-2 text-xs text-destructive hover:underline">
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "pillarlist") {
    const arr = Array.isArray(value) ? value : [];
    const upd = (i, part) => {
      const c = [...arr];
      c[i] = { ...(arr[i] || {}), ...part };
      onChange(c);
    };
    return (
      <div className="md:col-span-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <button
            type="button"
            data-testid={`field-${key}-add`}
            onClick={() => onChange([...arr, { title: { en: "", ar: "" }, body: { en: "", ar: "" } }])}
            className="text-xs font-semibold text-primary hover:underline"
          >
            + Add
          </button>
        </div>
        <div className="mt-2 space-y-3">
          {arr.map((item, i) => (
            <div key={i} className="grid gap-2 border border-border p-3" style={{ borderRadius: "var(--radius)" }}>
              <div className="grid grid-cols-2 gap-2">
                <Input value={item?.title?.en || ""} placeholder="Title EN" data-testid={`field-${key}-title-en-${i}`} onChange={(e) => upd(i, { title: { ...(item?.title || {}), en: e.target.value } })} />
                <Input dir="rtl" value={item?.title?.ar || ""} placeholder="Title AR" data-testid={`field-${key}-title-ar-${i}`} onChange={(e) => upd(i, { title: { ...(item?.title || {}), ar: e.target.value } })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Textarea rows={2} value={item?.body?.en || ""} placeholder="Body EN" data-testid={`field-${key}-body-en-${i}`} onChange={(e) => upd(i, { body: { ...(item?.body || {}), en: e.target.value } })} />
                <Textarea rows={2} dir="rtl" value={item?.body?.ar || ""} placeholder="Body AR" data-testid={`field-${key}-body-ar-${i}`} onChange={(e) => upd(i, { body: { ...(item?.body || {}), ar: e.target.value } })} />
              </div>
              <button type="button" data-testid={`field-${key}-remove-${i}`} onClick={() => onChange(arr.filter((_, x) => x !== i))} className="justify-self-end px-2 text-xs text-destructive hover:underline">
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "select") {
    return (
      <div>
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <select
          className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
          value={value || field.options[0].value}
          data-testid={`field-${key}-select`}
          onChange={(e) => onChange(e.target.value)}
        >
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  if (type === "gallerylist") {
    const arr = Array.isArray(value) ? value : [];
    const upd = (i, part) => {
      const c = [...arr];
      c[i] = { ...(arr[i] || {}), ...part };
      onChange(c);
    };
    return (
      <div className="md:col-span-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <button
            type="button"
            data-testid={`field-${key}-add`}
            onClick={() => onChange([...arr, { image: "", caption: { en: "", ar: "" } }])}
            className="text-xs font-semibold text-primary hover:underline"
          >
            + Add image
          </button>
        </div>
        <div className="mt-2 space-y-3">
          {arr.map((item, i) => (
            <div key={i} className="grid gap-2 border border-border p-3" style={{ borderRadius: "var(--radius)" }}>
              <MediaField
                accept="image"
                compact
                testKey={`${key}-image-${i}`}
                value={item?.image || ""}
                onChange={(v) => upd(i, { image: v })}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={item?.caption?.en || ""}
                  placeholder="Caption EN"
                  data-testid={`field-${key}-caption-en-${i}`}
                  onChange={(e) => upd(i, { caption: { ...(item?.caption || {}), en: e.target.value } })}
                />
                <Input
                  dir="rtl"
                  value={item?.caption?.ar || ""}
                  placeholder="Caption AR"
                  data-testid={`field-${key}-caption-ar-${i}`}
                  onChange={(e) => upd(i, { caption: { ...(item?.caption || {}), ar: e.target.value } })}
                />
              </div>
              <button
                type="button"
                data-testid={`field-${key}-remove-${i}`}
                onClick={() => onChange(arr.filter((_, x) => x !== i))}
                className="justify-self-end px-2 text-xs text-destructive hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "number") {
    return (
      <div>
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <Input
          className="mt-1.5"
          type="number"
          min={field.min ?? 0}
          max={field.max}
          value={value ?? ""}
          data-testid={`field-${key}`}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        />
      </div>
    );
  }

  if (type === "switch") {
    return (
      <div className="flex items-center justify-between gap-4 md:col-span-2">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${key}-switch`} />
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div className="md:col-span-2">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <Textarea rows={3} className="mt-1.5" value={value || ""} data-testid={`field-${key}`} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }

  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input className="mt-1.5" value={value || ""} data-testid={`field-${key}`} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
